/**
 * operators.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { ServerDataType } from "../common/data/data-types";

export type FilterNodeType = "value" | "variable" | "expression" | "argument" | null;

/**
 * The operator name is the canonical client name for the operator, used to
 * retrieve the faux-enumerated class.
 */
export type OperatorName = "and" | "or" | "is" | "not" | "equals" | "not equals"
    | "less than" | "less than or equal" | "greater than"
    | "greater than or equal" | "size of" | "contains" | "element of"
    | "contains text" | "add" | "subtract" | "multiply" | "divide" | "negation"
    | "modulus" | "absolute value" | "is answered" | "is not answered"
    | "all of";

/**
 * A signature describes one possible combination of operand and return type.
 */
export interface OperatorSignature
{
    left : ServerDataType;
    returns : ServerDataType;
}

/**
 * Extends the standard signature by adding a unique type for the right operand.
 */
export interface BinaryOperatorSignature extends OperatorSignature
{
    right : ServerDataType;
}

/**
 * Construct a list of signatures from a list of operand types and, optionally,
 * a distinct return type.
 * @param inTypes Array of operand types to generate signatures for.
 * @param outType A custom return type for all signatures. If omitted, the
 *                return types will match the operand types for all entries.
 */
function single (inTypes : ServerDataType[], outType? : ServerDataType)
    : OperatorSignature[]
{
    return inTypes.map((inType : ServerDataType) => ({
        left : inType,
        returns : outType || inType
    }));
}
/**
 * Construct a list of binary signatures from an operand type list and a
 * distinct return type.
 * @param inTypes Array of operand types to apply to BOTH operands.
 * @param outType A custom return type for all signatures. If omitted, the
 *                return types will match the operand types for all entries.
 */
function mirror (inTypes : ServerDataType[], outType? : ServerDataType)
    : BinaryOperatorSignature[]
{
    return inTypes.map((inType : ServerDataType) => ({
        left : inType,
        right : inType,
        returns : outType || inType
    }));
}

/**
 * Small interface to indicate whether each operand is a collection or not.
 */
interface ArgCollectFlags
{
    left : boolean;
    right? : boolean;
}

interface Readable
{
    toReadable : () => string;
}

interface CheckableNode
{
    nodeType : FilterNodeType;
}
interface OperatorHostNode extends CheckableNode
{
    operands : CheckableNode[];
    root : boolean;
}

/**
 * An aggregation {@link Operator}, used in the creation of filters via
 * {@link FilterNode}s.
 */
export abstract class Operator
{
    // The unique canonical client name of this operator
    public abstract name : OperatorName;

    // The compressed mathematical symbol to be used in sting embeddings.
    public abstract symbol : string;

    // The unique name recognized by the server for this operator.
    public abstract protocolName : string;

    // The number of operands, currently limited to 1 or 2.
    public abstract cardinality : number;

    // The list of all signatures (combinations of operand and return types)
    // that this operator can support.
    public abstract signature : OperatorSignature[];

    // Flags indicating which operands are expected to be collection types.
    public abstract collectionArguments : ArgCollectFlags;

    // Whether the UI will allow a user to choose this operator, or whether it
    // is "hidden" and can only be created by special actions.
    public userSelectable : boolean = true;

    // Retrieve a human-readable string labelling the operation
    public label (left : Readable, right? : Readable[]) : string
    {
        if (this.isBinary())
        {
            return `${left.toReadable()} ${this.symbol} ${right[0].toReadable()}`;
        }
        else
        {
            return `${this.symbol} ${left.toReadable()}`;
        }
    }

    // Whether the operator is binary (has 2 operands).
    public isBinary () : boolean
    {
        return this.cardinality === 2;
    }

    // Whether the operator is unary (has 2 operands).
    public isUnary () : boolean
    {
        return this.cardinality === 1;
    }

    // Whether the operator can support a varying number of arguments.
    // NOTE: This functionality is currently unused due to limitations, both
    // in the server AND client.
    public isVariableArity () : boolean
    {
        return this.cardinality === 0;
    }

    /**
     * Perform additional checks as to whether a host node is a valid site for
     * this particular operator. This is a catch-all for more complicated
     * behaviors we want our operators to enforce that aren't easily encoded
     * into the bare-bones type system.
     * @param host The node attempting to attach this operator
     */
    public accepts (host : OperatorHostNode) : boolean
    {
        return true;
    }

    // A list of all data types supported by filter operators
    public static ALL_TYPES : ServerDataType[] = [
        "boolean", "string", "float", "integer", "whole number",
        "natural number", "date"
    ];

    // A list of all numeric types supported by filter operators
    public static NUMERIC_TYPES : ServerDataType[] = [
        "float", "integer", "whole number", "natural number"
    ];

    // A list of data types that can be ordered by order operators
    public static ORDERED_TYPES : ServerDataType[] = [
        "string", "float", "integer", "whole number",
        "natural number", "date"
    ];
}

// An operator that accepts only singletons (not collections)
export abstract class SingletonOperator extends Operator
{
    public collectionArguments : ArgCollectFlags = {left: false, right: false};
}

export abstract class LogicalOperator extends SingletonOperator
{
    public readonly signature : BinaryOperatorSignature[] = mirror(["boolean"]);
    public label (left : Readable, right : Readable[]) : string
    {
        return `${left.toReadable()} ${this.name} ${right[0].toReadable()}`;
    }
}

export class And extends LogicalOperator
{
    public readonly name : OperatorName = "and";
    public readonly symbol : string = "∧";
    public readonly protocolName : string = `_${this.symbol}_`;
    // public readonly cardinality : number = 0;
    public readonly cardinality : number = 2;
}

export class ClientOnlyMultiAnd extends SingletonOperator
{
    public readonly name : OperatorName = "all of";
    public readonly symbol : string = "all of";
    public readonly userSelectable : boolean = false;
    public label (left : Readable, right? : Readable[]) : string
    {
        const rest : string =
            (right && right.length > 0)
            ? ", " + right.map(
                (r : Readable) => r.toReadable()).join(", ")
            : "";
        return `all of [${left.toReadable()}${rest}]`;
    }
    public get protocolName () : never
    {
        throw new Error("Marshalling error: the 'all of' op is client-only!");
    }
    public accepts (node : OperatorHostNode) : boolean
    {
        return node.root;
    }
    public readonly cardinality : number = 0;
    public readonly signature : BinaryOperatorSignature[] = mirror(["boolean"]);
}

export class Or extends LogicalOperator
{
    public readonly name : OperatorName = "or";
    public readonly symbol : string = "∨";
    public readonly protocolName : string = `_${this.symbol}_`;
    // public readonly cardinality : number = 0;
    public readonly cardinality : number = 2;
}

export class Not extends LogicalOperator
{
    public readonly name : OperatorName = "not";
    public readonly symbol : string = "¬";
    public readonly protocolName : string = `${this.symbol}_`;
    public readonly cardinality : number = 1;
    public label (left : Readable) : string
    {
        return `<strong>${this.name}</strong> ${left.toReadable()}`;
    }
}

export class Answered extends SingletonOperator
{
    public readonly name : OperatorName = "is answered";
    public readonly symbol : string = "∃";
    public readonly protocolName : string = `_${this.name}`;
    public readonly signature : OperatorSignature[] =
        single(Operator.ALL_TYPES, "boolean");
    public readonly cardinality : number = 1;
    public accepts (host : OperatorHostNode) : boolean
    {
        if (host.operands.length === 1)
        {
            return (host.operands[0].nodeType === "variable");
        }
        else
        {
            return true;
        }
    }
}
export class Unanswered extends SingletonOperator
{
    public readonly name : OperatorName = "is not answered";
    public readonly symbol : string = "∄";
    public readonly protocolName : string = `_${this.name}`;
    public readonly signature : OperatorSignature[] =
        single(Operator.ALL_TYPES, "boolean");
    public readonly cardinality : number = 1;
    public accepts (host : OperatorHostNode) : boolean
    {
        if (host.operands.length === 1)
        {
            return (host.operands[0].nodeType === "variable");
        }
        else
        {
            return true;
        }
    }
}

export class Is extends SingletonOperator
{
    public readonly name : OperatorName = "is";
    public readonly symbol : string = "is";
    public readonly cardinality : number = 1;
    public readonly userSelectable : boolean = false;
    public get protocolName () : never
    {
        throw new Error("Marshalling error: the 'is' operator is client-only!");
    }
    public readonly signature : OperatorSignature[] =
        single(Operator.ALL_TYPES);
}

export abstract class EqualityOperator extends SingletonOperator
{
    public readonly cardinality : number = 2;
    public readonly typeSpecificNames : Map<ServerDataType, string> = new Map();
    // TODO: re-implement the type-specific name stuff
}

export class Equals extends EqualityOperator
{
    public readonly name : OperatorName = "equals";
    public readonly symbol : string = "=";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.ALL_TYPES, "boolean");
}

export class NotEquals extends EqualityOperator
{
    public readonly name : OperatorName = "not equals";
    public readonly symbol : string = "≠";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.ALL_TYPES, "boolean");
}

export class LessThan extends EqualityOperator
{
    public readonly name : OperatorName = "less than";
    public readonly symbol : string = "<";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly typeSpecificNames : Map<ServerDataType, string> =
        new Map<ServerDataType, string>([
            ["string", "alphabetically before"],
            ["date", "before"]
        ]);
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.ORDERED_TYPES, "boolean");
}

export class LessThanOrEqual extends EqualityOperator
{
    public readonly name : OperatorName = "less than or equal";
    public readonly symbol : string = "≤";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly typeSpecificNames : Map<ServerDataType, string> =
        new Map<ServerDataType, string>([
            ["string", "matches or alphabetically before"],
            ["date", "before or on"]
        ]);
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.ORDERED_TYPES, "boolean");
}

export class GreaterThan extends EqualityOperator
{
    public readonly name : OperatorName = "greater than";
    public readonly symbol : string = ">";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly typeSpecificNames : Map<ServerDataType, string> =
        new Map<ServerDataType, string>([
            ["string", "alphabetically after"],
            ["date", "after"]
        ]);
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.ORDERED_TYPES, "boolean");
}

export class GreaterThanOrEqual extends EqualityOperator
{
    public readonly name : OperatorName = "greater than or equal";
    public readonly symbol : string = "≥";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly typeSpecificNames : Map<ServerDataType, string> =
        new Map<ServerDataType, string>([
            ["string", "matches or alphabetically after"],
            ["date", "on or after"]
        ]);
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.ORDERED_TYPES, "boolean");
}

export class SizeOf extends Operator
{
    public readonly name : OperatorName = "size of";
    public readonly symbol : string = "size of";
    public readonly protocolName : string = `${this.symbol}_`;
    public readonly cardinality : number = 1;
    public readonly signature : OperatorSignature[] =
        single(Operator.ALL_TYPES, "whole number");
    public readonly collectionArguments : ArgCollectFlags = {left: true};
}

// TODO: fix issue where collectionArguments check is failing:
// reproducible by making a string node and then still being able to select this
export class Contains extends Operator
{
    public readonly name : OperatorName = "contains";
    public readonly symbol : string = "∋";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly cardinality : number = 2;
    public readonly signature : OperatorSignature[] =
        mirror(Operator.ALL_TYPES, "boolean");
    public readonly collectionArguments : ArgCollectFlags =
        {left: true, right: false};
}

export class ElementOf extends Operator
{
    public readonly name : OperatorName = "element of";
    public readonly symbol : string = "∈";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly cardinality : number = 2;
    public readonly signature : OperatorSignature[] =
        mirror(Operator.ALL_TYPES, "boolean");
    public readonly collectionArguments : ArgCollectFlags =
        {left: false, right: true};
}

// export class AtIndex extends Operator
// {
//     public readonly name : OperatorName = "at index";
//     public readonly symbol : string = "@";
//     public readonly cardinality : number = 2;
//     public readonly signature : OperatorSignature[] =
//         Operator.ALL_TYPES.map((t : ServerDataType) => ({
//             left: t,
//             right: "natural number",
//             returns: t
//         }));
//     public readonly collectionArguments : ArgCollectFlags =
//         {left: true, right: false};
// }

export class Substring extends SingletonOperator
{
    public readonly name : OperatorName = "contains text";
    public readonly symbol : string = "has substring";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly cardinality : number = 2;
    public readonly signature : BinaryOperatorSignature[] =
        [{left: "string", right: "string", returns: "boolean"}];
}

export abstract class ArithmaticOperator extends SingletonOperator
{
    public readonly cardinality : number = 2;
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.NUMERIC_TYPES);
}

export class Add extends ArithmaticOperator
{
    public readonly name : OperatorName = "add";
    public readonly symbol : string = "+";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.NUMERIC_TYPES).concat(
            {left: "date", right: "integer", returns: "date"});
}

export class Subtract extends ArithmaticOperator
{
    public readonly name : OperatorName = "subtract";
    public readonly symbol : string = "-";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.NUMERIC_TYPES).concat(
            {left: "date", right: "integer", returns: "date"});
}

export class Multiply extends ArithmaticOperator
{
    public readonly name : OperatorName = "multiply";
    public readonly symbol : string = "×";
    public readonly protocolName : string = `_${this.symbol}_`;
}

export class Divide extends ArithmaticOperator
{
    public readonly name : OperatorName = "divide";
    public readonly symbol : string = "÷";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly signature : BinaryOperatorSignature[] =
        mirror(Operator.NUMERIC_TYPES, "float");
}

export class Negation extends SingletonOperator
{
    public readonly name : OperatorName = "negation";
    public readonly symbol : string = "⁻";
    public readonly protocolName : string = `${this.symbol}_`;
    public readonly cardinality : number = 1;
    public readonly signature : OperatorSignature[] = [
        {left: "float", returns: "float"},
        {left: "integer", returns: "integer"},
        {left: "whole number", returns: "integer"},
        {left: "natural number", returns: "integer"}
    ];
}

export class AbsoluteValue extends SingletonOperator
{
    public readonly name : OperatorName = "absolute value";
    public readonly symbol : string = "abs";
    public readonly protocolName : string = `|_|`;
    public readonly cardinality : number = 1;
    public readonly signature : OperatorSignature[] =
        single(Operator.NUMERIC_TYPES);
    public label (left : Readable) : string
    {
        return `|${left.toReadable()}|`;
    }
}

export class Modulus extends ArithmaticOperator
{
    public readonly name : OperatorName = "modulus";
    public readonly symbol : string = "mod";
    public readonly protocolName : string = `_${this.symbol}_`;
    public readonly signature : BinaryOperatorSignature[] =
        mirror(["integer", "natural number", "whole number"]);
}

export class Operators
{
    public static All : Operator[] = [
        new And(),
        new Or(),
        new Not(),
        new Is(),
        new Equals(),
        new NotEquals(),
        new LessThan(),
        new LessThanOrEqual(),
        new GreaterThan(),
        new GreaterThanOrEqual(),
        new SizeOf(),
        new Contains(),
        new ElementOf(),
        // new AtIndex(),
        new Substring(),
        new Add(),
        new Subtract(),
        new Multiply(),
        new Divide(),
        new Negation(),
        new Modulus(),
        new AbsoluteValue(),
        new Answered(),
        new Unanswered(),
        new ClientOnlyMultiAnd()
    ];

    public static get (name : string) : Operator
    {
        return Operators.All.find((op : Operator) => op.name === name);
    }

    public static getByProtocol (protocolName : string) : Operator
    {
        return Operators.All.find(
            (op : Operator) =>
                (op.name !== "is" && op.protocolName === protocolName));
    }

    public static byReturn (type : ServerDataType) : Operator[]
    {
        return Operators.All.filter((op : Operator) => op.signature.some(
            (sig : OperatorSignature) => sig.returns === type));
    }
}

export const INEQUALITIES : Operator[] = Operators.All.filter(
    (op : Operator) => op instanceof EqualityOperator);



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/operators.ts